import { ExpandMoreRounded } from '@mui/icons-material';
import {
  Checkbox,
  IconButton,
  SxProps,
  TableCell,
  TableRow,
  Theme,
  Typography,
  alpha,
  styled,
} from '@mui/material';
import { MouseEvent } from 'react';
import { ColumnCell, ColumnDef } from './Column';
import { RowSelectionState } from './useRowSelection';

export interface PrimaryRowProps<R, C> {
  columns: readonly ColumnDef<R, C>[];
  row: R;
  rowKey: string;
  context: C;
  rowSelection?: RowSelectionState;
  rowExpansion?: RowSelectionState;
  columnPadding?: number;
  disableStripe?: boolean;
  onRowClick?: (rowKey: string) => void;
  disableRow?: boolean;
  disabledRowSelection?: boolean;
}

export function PrimaryRow<R, C>({
  columns,
  row,
  rowKey,
  context,
  rowSelection,
  rowExpansion,
  columnPadding,
  disableStripe = false,
  disableRow,
  disabledRowSelection,
  onRowClick,
}: PrimaryRowProps<R, C>) {
  function onPrimaryRowClick(e: MouseEvent<HTMLTableRowElement>) {
    if (
      e.target instanceof Element &&
      (e.target.closest('input,button') || !e.currentTarget.contains(e.target))
    ) {
      return;
    }

    if (onRowClick) {
      onRowClick(rowKey);

      return;
    }
    if (!disabledRowSelection) (rowExpansion ?? rowSelection)?.toggle(rowKey);
  }

  return (
    <TableRow
      onClick={onPrimaryRowClick}
      className={disableStripe ? 'stripe-disabled' : undefined}
      sx={{
        backgroundColor: disableRow ? 'rgba(0,0,0,0.1)' : 'inherit',
        opacity: disableRow ? 0.5 : 1,
        cursor: onRowClick ? 'pointer' : 'default',
      }}
    >
      {/* Selection checkbox column */}
      {rowSelection && (
        <TableCell sx={rowExpansion ? { display: 'flex', gap: 1 } : {}}>
          <Checkbox
            checked={rowSelection.selectedKeys.has(rowKey)}
            onChange={() => rowSelection.toggle(rowKey)}
            disabled={disabledRowSelection}
            sx={{ p: 0 }}
          />
          {/* "Expand details" button column */}
          {rowExpansion && (
            <IconButton onClick={() => rowExpansion.toggle(rowKey)}>
              <ExpandMore $expanded={rowExpansion.selectedKeys.has(rowKey)} />
            </IconButton>
          )}
        </TableCell>
      )}

      {/* ColumnDef columns */}
      {columns.map((columnDef, i) => (
        <ColumnCell
          key={i}
          columnDef={columnDef}
          row={row}
          context={context}
          sx={columnDef.sx}
        />
      ))}

      {/* Padding if table has more detail than primary columns */}
      {!!columnPadding && <TableCell colSpan={columnPadding} />}
    </TableRow>
  );
}

interface ExpandMoreProps {
  $expanded: boolean;
}

const ExpandMore = styled(ExpandMoreRounded)<ExpandMoreProps>(
  ({ $expanded, theme }) => ({
    transition: theme.transitions.create('transform', { duration: 100 }),
    transform: $expanded ? 'rotate(180deg)' : undefined,
  }),
);

export interface DetailRowProps<R, C> {
  columns: readonly ColumnDef<R, C>[];
  row: R;
  context: C;
  tableHasRowSelection: boolean;
  columnPadding: number;
  showHeaders?: boolean;
}

export function DetailRow<R, C>({
  columns,
  row,
  context,
  tableHasRowSelection,
  columnPadding,
  showHeaders = false,
}: DetailRowProps<R, C>) {
  const sxDetailRow = (theme: Theme): Record<string, string> => ({
    backgroundColor: alpha(theme.palette.background.paper, 0.5),
  });
  const detailSxTableCell: SxProps<Theme> = {
    '&.MuiTableCell-body.MuiTableCell-root': sxDetailRow,
  };

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {showHeaders ? (
        <TableRow sx={sxDetailRow}>
          {tableHasRowSelection && <TableCell sx={detailSxTableCell} />}
          {columns.map((columnDef, i) => (
            <TableCell
              key={`header-${i}`}
              sx={{
                ...detailSxTableCell,
                backgroundColor: (theme) => theme.palette.grey[200],
              }}
            >
              <Typography variant="h6" color="text.secondary" noWrap>
                {columnDef.header(context)}
              </Typography>
            </TableCell>
          ))}
          {!!columnPadding && (
            <TableCell colSpan={columnPadding} sx={detailSxTableCell} />
          )}
          <TableCell sx={detailSxTableCell} />
        </TableRow>
      ) : (
        <TableRow sx={sxDetailRow}>
          {/* Selection checkbox column */}
          {tableHasRowSelection && <TableCell sx={detailSxTableCell} />}

          {/* ColumnDef columns */}
          {columns.map((columnDef, i) => (
            <ColumnCell
              key={i}
              columnDef={columnDef}
              row={row}
              context={context}
              sx={detailSxTableCell}
            />
          ))}

          {/* Padding if table has more primary than detail columns */}
          {!!columnPadding && (
            <TableCell colSpan={columnPadding} sx={detailSxTableCell} />
          )}

          {/* Padding for "Expand details" button column */}
          <TableCell sx={detailSxTableCell} />
        </TableRow>
      )}
    </>
  );
}
